// Google Calendar integration utilities

// Dr. Deepika Sinha's Google Calendar ID
// Replace with actual calendar ID from Google Calendar settings
const DOCTOR_CALENDAR_ID = process.env.REACT_APP_DOCTOR_CALENDAR_ID || 'shubhammshr621@gmail.com';
const GOOGLE_CALENDAR_API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY;

// Working hours from ContactModal: Tuesday-Saturday 4:00-6:00 PM, Sunday 10:00 AM-7:00 PM
const WORKING_HOURS = {
  2: { start: 16, end: 18 }, // Tuesday
  3: { start: 16, end: 18 }, // Wednesday  
  4: { start: 16, end: 18 }, // Thursday
  5: { start: 16, end: 18 }, // Friday
  6: { start: 16, end: 18 }, // Saturday
  0: { start: 10, end: 19 }, // Sunday
};

// Appointment durations in minutes
const APPOINTMENT_DURATIONS = {
  consultation: 30,
  followup: 15,
  procedure: null // Non-deterministic, will be handled separately
};

export const createGoogleCalendarEvent = ({
  title = "Aesthetic Consultation with Dr. Deepika Sinha",
  details = "Consultation appointment at Dr. Deepika Sinha's Aesthetic Clinic in Bhopal. Please arrive 15 minutes early for your appointment.",
  location = "1st floor, 60, Akriti Ecocity Road, Akriti Ecocity, Bawadiya Kalan, Bhopal",
  startDate = null,
  endDate = null,
  duration = 60 // in minutes
}) => {
  // If no dates provided, create a suggested appointment time (next business day at 10 AM)
  if (!startDate) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // If tomorrow is weekend, move to next Monday
    if (tomorrow.getDay() === 0) { // Sunday
      tomorrow.setDate(tomorrow.getDate() + 1);
    } else if (tomorrow.getDay() === 6) { // Saturday
      tomorrow.setDate(tomorrow.getDate() + 2);
    }
    
    // Set to 10:00 AM
    tomorrow.setHours(10, 0, 0, 0);
    startDate = tomorrow;
  }

  if (!endDate) {
    endDate = new Date(startDate.getTime() + (duration * 60 * 1000));
  }

  // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ format)
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    details: details,
    location: location,
    sf: true,
    output: 'xml'
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const openGoogleCalendar = (eventDetails = {}) => {
  const calendarUrl = createGoogleCalendarEvent(eventDetails);
  window.open(calendarUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
};

// Check if doctor is available using Google Calendar API
export const checkCalendarAvailability = async (startTime, endTime) => {
  if (!GOOGLE_CALENDAR_API_KEY) {
    console.warn('Google Calendar API key not configured. Using mock availability.');
    return Math.random() > 0.3; // Mock: 70% availability
  }

  try {
    const timeMin = startTime.toISOString();
    const timeMax = endTime.toISOString();
    
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(DOCTOR_CALENDAR_ID)}/events?` +
      new URLSearchParams({
        key: GOOGLE_CALENDAR_API_KEY,
        timeMin,
        timeMax,
        singleEvents: 'true',
        orderBy: 'startTime'
      })
    );

    if (!response.ok) {
      throw new Error(`Calendar API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Check if any events overlap with requested time
    const hasConflict = data.items.some(event => {
      if (!event.start?.dateTime || !event.end?.dateTime) return false;
      
      const eventStart = new Date(event.start.dateTime);
      const eventEnd = new Date(event.end.dateTime);
      
      // Check for time overlap
      return (startTime < eventEnd && endTime > eventStart);
    });

    return !hasConflict;
  } catch (error) {
    console.error('Error checking calendar availability:', error);
    return false; // Conservative approach: assume unavailable on error
  }
};

// Generate time slots based on working hours and appointment type
const generateTimeSlotsForDay = (date, appointmentType) => {
  const dayOfWeek = date.getDay();
  const workingHours = WORKING_HOURS[dayOfWeek];
  
  if (!workingHours) return []; // Day off
  
  const slots = [];
  const duration = APPOINTMENT_DURATIONS[appointmentType];
  
  if (appointmentType === 'procedure') {
    // For procedures, only offer start times (duration is non-deterministic)
    const morningSlot = new Date(date);
    if (dayOfWeek === 0) { // Sunday
      morningSlot.setHours(10, 0, 0, 0);
      slots.push({
        date: date.toDateString(),
        time: "10:00 AM",
        datetime: morningSlot,
        duration: null,
        type: appointmentType
      });
    }
    
    const afternoonSlot = new Date(date);
    afternoonSlot.setHours(workingHours.start, 0, 0, 0);
    slots.push({
      date: date.toDateString(),
      time: `${workingHours.start}:00 ${workingHours.start >= 12 ? 'PM' : 'AM'}`,
      datetime: afternoonSlot,
      duration: null,
      type: appointmentType
    });
    
    return slots;
  }
  
  // For consultation and follow-up with fixed durations
  const slotInterval = Math.max(duration, 15); // Minimum 15-minute intervals
  
  for (let hour = workingHours.start; hour < workingHours.end; hour++) {
    for (let minute = 0; minute < 60; minute += slotInterval) {
      const slotTime = new Date(date);
      slotTime.setHours(hour, minute, 0, 0);
      
      // Check if slot fits within working hours
      const slotEnd = new Date(slotTime.getTime() + duration * 60000);
      if (slotEnd.getHours() > workingHours.end || 
          (slotEnd.getHours() === workingHours.end && slotEnd.getMinutes() > 0)) {
        continue;
      }
      
      // Skip past times
      if (slotTime <= new Date()) continue;
      
      const timeString = slotTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      slots.push({
        date: date.toDateString(),
        time: timeString,
        datetime: slotTime,
        duration: duration,
        type: appointmentType
      });
    }
  }
  
  return slots;
};

// Get available slots with real calendar integration
export const getAvailableSlots = async (appointmentType = 'consultation') => {
  const slots = [];
  const today = new Date();
  
  // Generate slots for next 14 days
  for (let i = 1; i <= 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    const daySlots = generateTimeSlotsForDay(date, appointmentType);
    slots.push(...daySlots);
  }
  
  // Check availability for each slot
  const availabilityPromises = slots.map(async (slot) => {
    if (slot.duration === null) {
      // For procedures, just check if the start time is free
      const endTime = new Date(slot.datetime.getTime() + 30 * 60000); // Check 30 min window
      slot.available = await checkCalendarAvailability(slot.datetime, endTime);
    } else {
      const endTime = new Date(slot.datetime.getTime() + slot.duration * 60000);
      slot.available = await checkCalendarAvailability(slot.datetime, endTime);
    }
    return slot;
  });
  
  const slotsWithAvailability = await Promise.all(availabilityPromises);
  
  // Return only available slots, limited to 20
  return slotsWithAvailability
    .filter(slot => slot.available)
    .slice(0, 20);
};